import { useEffect, useState } from 'react';
import type { CameraFeature } from '@tsr/shared-types';
import { CameraMedia } from './CameraMedia';
import { NewsMedia } from './NewsMedia';
import { DroneMedia } from './DroneMedia';
import { useElementSize } from './lib/useElementSize';
import type { PinnedFeed } from './pinnedFeeds';

/**
 * Bounded, ADAPTIVE media region for the operations panel (#208, spec §2).
 *
 * Pinned cameras / news fill the region's reserved area — they grow to use the
 * space rather than sitting at a fixed thumbnail size, but they never crop the
 * frame (`object-contain` inside each tile) and they never compress the
 * protected queues (the region's area is reserved by the panel layout, not
 * stolen from the queues). The arrangement adapts to the pin count:
 *   • 1 pin   → one tile fills the entire region.
 *   • 2 pins  → two cells of the 2×2 grid, side-by-side (one row). The earlier
 *               "split to the larger contained frame" heuristic could pick a
 *               short stacked pair that letterboxed badly on a wide region, so a
 *               2-feed panel now mirrors the 3–4 grid: each tile is half-width at
 *               full region height, a proper ~16:9 size on a landscape panel.
 *   • 3-4     → 2×2 grid, tiles fill the cells.
 *   • 5+      → 2×2 grid (4 visible) + scroll; tiles do NOT shrink below the
 *               grid size ("4 shown · N more — scroll").
 * Portrait is a single-column reel sized to each feed's NATURAL ~16:9 height for
 * the panel width (#214-followup): it shows as many feeds as FIT at that usable
 * size and scrolls the rest — never compressing two into thin slivers. A single
 * pin fills the whole region; if two can't both fit usably it shows one uncropped
 * + scroll for the other (supersedes the forced 50/50 split). Up to 4 visible.
 */

export const MAX_VISIBLE_TILES = 4;

/** How many tiles are visible before the region scrolls. */
export function visibleTileCount(pinCount: number): number {
  return Math.min(pinCount, MAX_VISIBLE_TILES);
}

/** Overflow count (pins reachable only by scrolling past the visible cap). */
export function overflowTileCount(pinCount: number): number {
  return Math.max(0, pinCount - MAX_VISIBLE_TILES);
}

const GAP = 8;
// Fallback tile height before the region has been measured (SSR / no
// ResizeObserver / first paint). Once measured, tiles size to fill the region.
const FALLBACK_TILE_H = 192;
// Target media aspect (16:9) used to size a portrait reel tile to its NATURAL
// height for the panel width, plus the tile's own label-bar chrome — so the
// height test is "does the FRAME fit at a usable size," not just the cell.
const PORTRAIT_TILE_ASPECT = 16 / 9;
const PORTRAIT_TILE_CHROME = 28; // tile header (label + unpin) above the media

export interface MediaLayout {
  /** Grid columns. */
  cols: number;
  /** Rows that fit in the region without scrolling. */
  visibleRows: number;
  /** Tiles shown without scrolling (= min(pinCount, cols × visibleRows)). */
  visibleCount: number;
  /** Per-tile width in px (informational; the grid fills width via 1fr columns). */
  tileWidth: number;
  /** Per-tile (row track) height in px — what the grid actually applies. */
  tileHeight: number;
  /** Whether the region scrolls (more pins than the visible cap). */
  scroll: boolean;
}

/**
 * Pure layout solver for the adaptive media region. Given the region's measured
 * size and the pin count, returns the grid shape and per-tile pixel size that
 * fills the area without cropping (frames letterbox inside each tile) and
 * without shrinking tiles below the 2×2 size once the 4-cap is hit. Exported for
 * unit tests.
 */
export function mediaTileLayout(opts: {
  orientation: 'portrait' | 'landscape';
  pinCount: number;
  width: number;
  height: number;
  gap?: number;
}): MediaLayout {
  const { orientation, pinCount, width, height } = opts;
  const gap = opts.gap ?? GAP;
  const n = Math.max(0, pinCount);

  let cols: number;
  let visibleRows: number;

  if (n <= 1) {
    cols = 1;
    visibleRows = 1;
  } else if (orientation === 'portrait') {
    // Single-column reel sized to each feed's NATURAL ~16:9 height for the panel
    // width (#214-followup, spec §2): show as many feeds as FIT at that usable
    // size, scroll the rest — never compress two into thin slivers. This
    // supersedes the forced up-to-4 split, which letterboxed each feed into a
    // thin band on a short window. (1 pin is handled by the n<=1 branch above and
    // still fills the whole region.)
    cols = 1;
    const naturalTileH =
      width > 0 ? width / PORTRAIT_TILE_ASPECT + PORTRAIT_TILE_CHROME : FALLBACK_TILE_H;
    const fit =
      height > 0
        ? Math.max(1, Math.floor((height + gap) / (naturalTileH + gap)))
        : MAX_VISIBLE_TILES;
    // At most 4 visible, never more than fit at a usable size, never more than we have.
    visibleRows = Math.min(n, fit, MAX_VISIBLE_TILES);
  } else if (n === 2) {
    // Two cells of the 2×2 grid, side-by-side (one row): each tile is half-width
    // at full height — a proper ~16:9 size on a wide panel. (We dropped the
    // contained-frame heuristic: on a wide region it could choose a stacked pair
    // that letterboxed each feed into a thin band.)
    cols = 2;
    visibleRows = 1;
  } else {
    // 3-4 fill a 2×2 grid; 5+ keep the same 2×2 cell size and scroll.
    cols = 2;
    visibleRows = 2;
  }

  const capacity = cols * visibleRows;
  const visibleCount = Math.min(n, capacity);
  const scroll = n > capacity;
  const tileWidth = cols > 0 ? (width - (cols - 1) * gap) / cols : width;
  const tileHeight =
    visibleRows > 0 && height > 0
      ? (height - (visibleRows - 1) * gap) / visibleRows
      : FALLBACK_TILE_H;

  return { cols, visibleRows, visibleCount, tileWidth, tileHeight, scroll };
}

export function PinnedMediaRegion({
  pins,
  orientation,
  onUnpin,
}: {
  pins: PinnedFeed[];
  orientation: 'portrait' | 'landscape';
  onUnpin: (kind: PinnedFeed['kind'], id: string) => void;
}): JSX.Element {
  const [gridRef, { width, height }] = useElementSize<HTMLDivElement>();
  const overflow = overflowTileCount(pins.length);

  // No pins → a COMPACT add-feed affordance, not a big empty box (PART 1, spec §2).
  // The panel collapses the media region to a slim strip and lets the queues grow
  // to fill (PanelView owns that flex flip); here we render only the slim affordance.
  if (pins.length === 0) {
    const affordance = (
      <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 px-2 py-1.5">
        <button
          type="button"
          data-testid="media-add-feed"
          title="Pin a camera from the COP map"
          className="shrink-0 rounded-md border border-dashed border-slate-600 px-3 py-1 text-[12px] font-medium text-slate-300 transition-colors hover:border-slate-400 hover:text-slate-100"
        >
          + Add feed
        </button>
        <span data-testid="media-empty" className="text-[11px] text-slate-500">
          Pin a camera from the COP map · or a news / drone feed from the command bar
        </span>
      </div>
    );
    return orientation === 'landscape' ? (
      <section
        data-testid="media-region"
        className="flex h-full items-center justify-center"
      >
        {affordance}
      </section>
    ) : (
      <section data-testid="media-region" className="flex items-center justify-center">
        {affordance}
      </section>
    );
  }

  const layout = mediaTileLayout({
    orientation,
    pinCount: pins.length,
    width,
    height,
    gap: GAP,
  });

  return (
    <section data-testid="media-region" className="flex h-full min-h-0 flex-col">
      <header className="flex shrink-0 items-center justify-between px-1 pb-1">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-300">
          Pinned Feeds
        </h2>
        <span className="text-[11px] text-slate-400">
          {overflow > 0
            ? `${MAX_VISIBLE_TILES} shown · ${overflow} more — scroll`
            : `${pins.length} pinned`}
        </span>
      </header>
      <div
        ref={gridRef}
        data-testid="media-grid"
        data-cols={layout.cols}
        data-visible-rows={layout.visibleRows}
        className="grid min-h-0 flex-1 gap-2 overflow-y-auto"
        style={{
          gridTemplateColumns: `repeat(${layout.cols}, minmax(0, 1fr))`,
          // Explicit row track so the visible rows fill the region and a 5th+
          // tile scrolls instead of shrinking the existing four (spec §2).
          gridAutoRows: `${Math.max(1, Math.round(layout.tileHeight))}px`,
        }}
      >
        {pins.map((p) => (
          <PinnedTile key={`${p.kind}:${p.id}`} feed={p} onUnpin={onUnpin} />
        ))}
      </div>
    </section>
  );
}

function PinnedTile({
  feed,
  onUnpin,
}: {
  feed: PinnedFeed;
  onUnpin: (kind: PinnedFeed['kind'], id: string) => void;
}): JSX.Element {
  return (
    <div
      data-testid="media-tile"
      // h-full + min-h-0 makes the tile fill its grid cell (gridAutoRows sets the
      // height); overflow-hidden + contained media below means the frame never
      // crops — it letterboxes inside the tile.
      className="relative flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-slate-700 bg-slate-950"
    >
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-slate-800 px-2 py-1">
        <span className="min-w-0 truncate text-[11px] text-slate-200" title={feed.label}>
          {feed.label}
        </span>
        <button
          type="button"
          data-testid="media-tile-unpin"
          onClick={() => onUnpin(feed.kind, feed.id)}
          title="Unpin"
          className="shrink-0 rounded px-1 text-slate-400 hover:text-rose-300"
        >
          ✕
        </button>
      </div>
      <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden">
        {feed.kind === 'camera' ? (
          <PinnedCameraBody id={feed.id} />
        ) : feed.kind === 'drone' ? (
          // Ambient drone feed (#216) — same shared YouTube player as news; the
          // current live resolves at view time, contained so the frame never crops.
          <div className="h-full w-full overflow-hidden [&_iframe]:h-full [&_iframe]:w-full">
            <DroneMedia id={feed.id} label={feed.label} />
          </div>
        ) : (
          // Ambient news outlet (#182) — the shared YouTube player resolves its
          // current live at view time; contained so the frame never crops.
          <div className="h-full w-full overflow-hidden [&_iframe]:h-full [&_iframe]:w-full">
            <NewsMedia id={feed.id} label={feed.label} />
          </div>
        )}
      </div>
    </div>
  );
}

/** Lazily fetch the live camera feature by id, then render the shared player. */
function PinnedCameraBody({ id }: { id: string }): JSX.Element {
  const [camera, setCamera] = useState<CameraFeature | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setCamera(null);
    setFailed(false);
    void fetch(`/api/cameras/${id}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((feat: CameraFeature | null) => {
        if (cancelled) return;
        if (feat && (feat as { type?: string }).type === 'Feature') setCamera(feat);
        else setFailed(true);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (failed) {
    return <p className="p-2 text-[11px] text-slate-500">Feed unavailable.</p>;
  }
  if (!camera) {
    return <p className="p-2 text-[11px] text-slate-500">Loading…</p>;
  }
  // Pop-out disabled inside a tile — the panel IS the second screen. The media
  // fills the tile width and contains (letterboxes) so it never crops.
  return (
    <div className="h-full w-full overflow-hidden [&_img]:max-h-full [&_img]:w-full [&_img]:object-contain">
      <CameraMedia camera={camera} enablePopout={false} />
    </div>
  );
}
